import { useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtINR, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Paperclip, Info } from "@phosphor-icons/react";
import { Field } from "./Employees";
import ApprovalActionDialog from "@/components/ApprovalActionDialog";
import WorkflowToolbar from "@/components/WorkflowToolbar";
import Pagination from "@/components/Pagination";
import BulkPaidBar from "@/components/BulkPaidBar";

export default function Travel() {
  const { user } = useAuth();
  const canApply = user?.role !== "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const [paidSelected, setPaidSelected] = useState(new Set());
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const empty = { purpose: "", client_account_id: "", opportunity_id: "", from_location: "", to_location: "", start_date: "", end_date: "", hotel: 0, transport: 0, food: 0, local_conveyance: 0, remarks: "", attachment_ids: [], auto_pay_on_approval: false };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  const [filters, setFilters] = useState({ page: 1, page_size: 25 });
  const [total, setTotal] = useState(0);

  const load = () => {
    const params = { ...filters };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    return api.get("/travel", { params }).then((r) => {
      if (Array.isArray(r.data)) { setItems(r.data); setTotal(r.data.length); }
      else { setItems(r.data.items || []); setTotal(r.data.total || 0); }
    });
  };
  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/opportunities").then((r) => setOpportunities(r.data)).catch(() => {});
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    const { status, ...rest } = filters;  // eslint-disable-line no-unused-vars
    const params = { ...rest };
    Object.keys(params).forEach((k) => { if (!params[k] || k === "page" || k === "page_size") delete params[k]; });
    api.get("/travel/summary", { params }).then((r) => setSummary(r.data)).catch(() => {});
  }, [filters]);

  const filteredOpportunities = useMemo(() => {
    if (!form.client_account_id) return [];
    return opportunities.filter((o) => o.account_id === form.client_account_id);
  }, [form.client_account_id, opportunities]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); fd.append("category", "travel");
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      }
      setForm((f) => ({ ...f, attachment_ids: [...(f.attachment_ids || []), ...ids] }));
      toast.success(`${ids.length} file(s) attached`);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const removeAtt = (fid) => setForm((f) => ({ ...f, attachment_ids: (f.attachment_ids || []).filter((x) => x !== fid) }));

  const cancel = async (t) => {
    if (!window.confirm(`Cancel this travel request "${t.purpose}"?`)) return;
    try { await api.post(`/travel/${t.id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    if (!form.purpose || !form.from_location || !form.to_location || !form.start_date || !form.end_date) return toast.error("Fill required fields");
    const cb = { hotel: Number(form.hotel)||0, transport: Number(form.transport)||0, food: Number(form.food)||0, local_conveyance: Number(form.local_conveyance)||0 };
    const total = Object.values(cb).reduce((a,b)=>a+b, 0);
    try { await api.post("/travel", { ...form, cost_breakdown: cb, estimated_cost: total, auto_pay_on_approval: !!form.auto_pay_on_approval }); toast.success("Submitted"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = items;  // Backend already filtered by tab/status
  const counts = summary ? {
    all: summary.total_claims || 0,
    pending: (summary.pending || 0) + (summary.info_requested || 0),
    approved: summary.approved || 0,
    rejected: (summary.rejected || 0) + (summary.cancelled || 0),
    paid: summary.paid || 0,
  } : { all: total, pending: 0, approved: 0, rejected: 0, paid: 0 };

  const onTabChange = (v) => {
    setTab(v);
    // Rejected tab merges rejected+cancelled server-side isn't supported single-status,
    // so for "rejected" we send status=rejected (cancelled will be excluded from count parity)
    const map = { all: "", pending: "pending", approved: "approved", rejected: "rejected", paid: "paid" };
    setFilters((f) => ({ ...f, status: map[v] || "", page: 1 }));
  };
  const accountName = (id) => accounts.find((a) => a.id === id)?.company_name || "";
  const oppName = (id) => opportunities.find((o) => o.id === id)?.name || "";

  return (
    <div data-testid="travel-page">
      <PageHeader title="Travel Approval" description={canApply ? "Apply for business travel & track approvals." : "Review and approve travel requests from your team."} action={canApply && (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
          <DialogTrigger asChild>
            <Button onClick={() => setForm(empty)} className="btn-brand" data-testid="travel-new"><Plus size={16} className="mr-1.5" />New Request</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Travel Request</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Field label="Purpose *"><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} /></Field></div>
              <Field label="Client (Account)">
                <Select value={form.client_account_id || ""} onValueChange={(v) => setForm({ ...form, client_account_id: v, opportunity_id: "" })}>
                  <SelectTrigger data-testid="travel-account-select"><SelectValue placeholder="Pick an account…" /></SelectTrigger>
                  <SelectContent>
                    {accounts.length === 0 && <div className="px-3 py-2 text-xs opacity-60">No accounts yet.</div>}
                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Opportunity (optional)">
                <Select value={form.opportunity_id || ""} onValueChange={(v) => setForm({ ...form, opportunity_id: v })} disabled={!form.client_account_id}>
                  <SelectTrigger data-testid="travel-opportunity-select"><SelectValue placeholder={form.client_account_id ? (filteredOpportunities.length ? "Pick an opportunity…" : "No opportunities for this client") : "Select client first"} /></SelectTrigger>
                  <SelectContent>
                    {filteredOpportunities.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} — {o.stage}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Total Estimate (₹) — auto"><Input type="number" value={(Number(form.hotel||0)+Number(form.transport||0)+Number(form.food||0)+Number(form.local_conveyance||0)).toFixed(2)} readOnly /></Field>
              <Field label="Hotel (₹)"><Input type="number" data-testid="travel-hotel" value={form.hotel || 0} onChange={(e) => setForm({ ...form, hotel: e.target.value })} /></Field>
              <Field label="Transport (₹)"><Input type="number" data-testid="travel-transport" value={form.transport || 0} onChange={(e) => setForm({ ...form, transport: e.target.value })} placeholder="Train / Bus / Flight / Auto / Metro" /></Field>
              <Field label="Food (₹)"><Input type="number" data-testid="travel-food" value={form.food || 0} onChange={(e) => setForm({ ...form, food: e.target.value })} /></Field>
              <Field label="Local Conveyance (₹)"><Input type="number" data-testid="travel-conveyance" value={form.local_conveyance || 0} onChange={(e) => setForm({ ...form, local_conveyance: e.target.value })} /></Field>
              <Field label="From *"><Input value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} /></Field>
              <Field label="To *"><Input value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} /></Field>
              <Field label="Start Date *"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
              <Field label="End Date *"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
              <div className="sm:col-span-2"><Field label="Remarks"><Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field></div>
              <div className="sm:col-span-2">
                <Field label="Attachments (multiple files supported)">
                  <div className="flex items-center gap-2">
                    <Input type="file" multiple onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="travel-file" />
                    {uploading && <span className="text-xs text-slate-500">Uploading…</span>}
                  </div>
                  {(form.attachment_ids || []).length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-2 text-xs">
                      {form.attachment_ids.map((fid) => (
                        <li key={fid} className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                          <Paperclip size={12} className="text-[#fd7e14]" />
                          <a href={fileUrl(fid)} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">{fid.slice(0, 6)}…</a>
                          <button type="button" onClick={() => removeAtt(fid)} className="text-red-600 ml-1">×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Field>
              </div>
              <div className="sm:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="travel-autopay-label">
                  <input
                    type="checkbox"
                    className="rounded border-slate-400 h-4 w-4 accent-[#fd7e14]"
                    checked={!!form.auto_pay_on_approval}
                    onChange={(e) => setForm({ ...form, auto_pay_on_approval: e.target.checked })}
                    data-testid="travel-autopay"
                  />
                  <span>I have already paid for this travel — <b>auto mark as Paid once Finance Manager approves</b></span>
                </label>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="btn-brand" data-testid="travel-save">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )} />

      {!canApply && (
        <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40 p-3 text-sm">
          <Info size={18} className="text-amber-700 dark:text-amber-300 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-amber-900 dark:text-amber-200">Super Admin view</div>
            <div className="text-amber-800 dark:text-amber-300/90">As Super Admin you cannot apply for travel. Use the Approvals page to review and act on requests submitted by employees.</div>
          </div>
        </div>
      )}

      <WorkflowToolbar kind="travel" filters={filters} setFilters={setFilters} />

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="all" data-testid="travel-tab-all">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="travel-tab-pending">Pending ({counts.pending})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="travel-tab-approved">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="travel-tab-rejected">Rejected ({counts.rejected})</TabsTrigger>
          <TabsTrigger value="paid" data-testid="travel-tab-paid">Paid ({counts.paid})</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {isSuperAdmin && (
            <BulkPaidBar kind="travel" rows={filtered} selected={paidSelected} setSelected={setPaidSelected}
              onDone={() => setFilters((f) => ({ ...f }))} />
          )}
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                {isSuperAdmin && <TableHead className="w-10"></TableHead>}
                <TableHead>Purpose</TableHead><TableHead>Client / Opp.</TableHead><TableHead>Route</TableHead><TableHead>Dates</TableHead>
                <TableHead>Cost</TableHead><TableHead>Attachments</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((t) => {
                  const atts = t.attachment_ids && t.attachment_ids.length ? t.attachment_ids : (t.attachment_id ? [t.attachment_id] : []);
                  const canCancel = (t.applicant_id === user.id || user.role === "super_admin") && (t.status === "pending" || t.status === "info_requested");
                  const canSelectPaid = isSuperAdmin && t.status === "approved" && t.approval_id;
                  return (
                    <TableRow key={t.id}>
                      {isSuperAdmin && (
                        <TableCell>
                          {canSelectPaid && (
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-[#fd7e14]"
                              checked={paidSelected.has(t.approval_id)}
                              onChange={() => {
                                const s = new Set(paidSelected);
                                s.has(t.approval_id) ? s.delete(t.approval_id) : s.add(t.approval_id);
                                setPaidSelected(s);
                              }}
                              data-testid={`travel-paid-check-${t.id}`}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell><div className="font-medium">{t.purpose}</div><div className="text-xs text-slate-500">{t.remarks}</div></TableCell>
                      <TableCell className="text-sm">
                        <div>{t.client_name || accountName(t.client_account_id) || "—"}</div>
                        {t.opportunity_id && <div className="text-xs opacity-60 truncate max-w-[180px]">{oppName(t.opportunity_id)}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{t.from_location} → {t.to_location}</TableCell>
                      <TableCell className="text-sm">{fmtDate(t.start_date)} – {fmtDate(t.end_date)}</TableCell>
                      <TableCell className="font-medium">{fmtINR(t.estimated_cost)}</TableCell>
                      <TableCell className="text-xs space-x-1.5">
                        {atts.length === 0 ? "—" : atts.map((fid, i) => (
                          <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">View {atts.length > 1 ? i + 1 : ""}</a>
                        ))}
                      </TableCell>
                      <TableCell><StatusPill status={t.status} /></TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => setReviewId(t.id)} data-testid={`travel-review-${t.id}`}>Review</Button>
                        {canCancel && <Button size="sm" variant="outline" className="text-red-600" onClick={() => cancel(t)} data-testid={`travel-cancel-${t.id}`}>Cancel</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && <TableRow><TableCell colSpan={isSuperAdmin ? 9 : 8} className="text-center text-sm text-slate-500 py-10">No travel requests in this tab.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
          <Pagination total={total} filters={filters} setFilters={setFilters} />
        </TabsContent>
      </Tabs>

      <ApprovalActionDialog
        open={!!reviewId}
        onOpenChange={(o) => !o && setReviewId(null)}
        reqType="travel"
        reqId={reviewId}
        onDone={load}
      />
    </div>
  );
}

export const StatusPill = ({ status }) => {
  const m = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    paid: "bg-purple-100 text-purple-800",
    cancelled: "bg-slate-200 text-slate-700",
    info_requested: "bg-blue-100 text-blue-800",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m[status] || "bg-slate-100"}`}>{(status || "").replace(/_/g, " ")}</span>;
};
